import type { ReactNode } from 'react';
import { OutOfPolicyGlyph } from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiI18n, type GenUiKey } from '../../../shared/ui/i18n.js';
import { formatCurrency } from '@voll-intelligence/intelligence-ui-kit/checkout';
import type { ChatUiMoney } from '@voll-intelligence/genui-contract';
import './summary_foot.css';

interface SummaryFootProps {
  /** Rótulo do valor em destaque: `Por pessoa` no voo, `Total` nos demais. */
  readonly valueKey: GenUiKey;
  readonly value: ChatUiMoney;
  /** Uma linha de texto sob o total que não muda o papel dele («pelas 3
   *  noites» do hotel, «pelas 3 diárias» do carro). */
  readonly caption?: string;
  readonly inPolicy: boolean;
  readonly shieldAriaKey: GenUiKey;
  readonly shieldTitleKey: GenUiKey;
  /** O nome do escudo com o fato inteiro («Fora da política: 18 km, limite 15
   *  km»), no lugar das chaves — no `aria-label` e no tooltip. */
  readonly shieldLabel?: string;
  readonly actions: ReactNode;
}

/** O rodapé do card: o dinheiro à esquerda (com o escudo destructive colado
 *  quando estoura a política) e os gestos na ponta. */
export function SummaryFoot({
  valueKey,
  value,
  caption,
  inPolicy,
  shieldAriaKey,
  shieldTitleKey,
  shieldLabel,
  actions,
}: SummaryFootProps) {
  const { t, locale } = useGenUiI18n();
  return (
    <div className="pcf-foot">
      <span className="pcf-kv">
        <span className="pcf-kv-k">{t(valueKey)}</span>
        <span
          className="pcf-total-num"
          data-money-role="checkout-total"
          data-money-amount={value.amount}
          data-money-currency={value.currency}
        >
          {formatCurrency(value.amount, value.currency, locale)}
          {/* O escudo é o único portador do fato "fora da política" neste card, então
              ele precisa de NOME acessível — glifo sem rótulo esconde o fato de quem
              usa leitor de tela. Mesmo par da linha de ofertas (`fq-shield`): frase
              inteira no `aria-label`, rótulo curto no `title` do hover.
              O `title` vai como PROP do glifo, não num `title` do `<span>` que o
              embrulha: dois `title` aninhados fariam o navegador mostrar sempre o de
              dentro, calando este — `pcf-shield` sobrevive só pelo posicionamento. */}
          {!inPolicy && (
            <span className="pcf-shield">
              <OutOfPolicyGlyph
                label={shieldLabel ?? t(shieldAriaKey)}
                title={shieldLabel ?? t(shieldTitleKey)}
              />
            </span>
          )}
        </span>
        {caption && <span className="pcf-kv-k">{caption}</span>}
      </span>
      <span className="pcf-foot-spacer" />
      {actions}
    </div>
  );
}
